const POPOVER_SYNC_DELAYS_MS = [0, 16, 50, 100, 200, 400] as const;

const POPOVER_EDGE_PADDING_PX = 10;
const MIN_POPOVER_HEIGHT_PX = 160;
const MIN_BODY_HEIGHT_PX = 120;
const MOBILE_MAX_WIDTH_PX = 767;

function shouldConstrainMorePopoverHorizontal(calendarContainer: HTMLElement): boolean {
  return (
    window.innerWidth <= MOBILE_MAX_WIDTH_PX &&
    calendarContainer.closest('.sf-calendar--mobile-full') !== null
  );
}

function getCalendarBounds(calendarContainer: HTMLElement): DOMRect {
  const fc = calendarContainer.querySelector('.fc');
  if (fc instanceof HTMLElement) {
    return fc.getBoundingClientRect();
  }

  return calendarContainer.getBoundingClientRect();
}

function findMorePopover(calendarContainer: HTMLElement): HTMLElement | null {
  const inContainer = calendarContainer.querySelector('.fc-more-popover');
  if (inContainer instanceof HTMLElement) {
    return inContainer;
  }

  const inDocument = document.querySelector('.fc-more-popover');
  if (inDocument instanceof HTMLElement) {
    return inDocument;
  }

  return null;
}

function constrainMorePopover(popover: HTMLElement, calendarContainer: HTMLElement) {
  const calendarBounds = getCalendarBounds(calendarContainer);
  const popoverRect = popover.getBoundingClientRect();

  const header = popover.querySelector('.fc-popover-header');
  const headerHeight = header instanceof HTMLElement ? header.offsetHeight : 0;

  const spaceInCalendar = calendarBounds.bottom - popoverRect.top - POPOVER_EDGE_PADDING_PX;
  const spaceInViewport = window.innerHeight - popoverRect.top - POPOVER_EDGE_PADDING_PX;
  const maxPopoverHeight = Math.max(MIN_POPOVER_HEIGHT_PX, Math.min(spaceInCalendar, spaceInViewport));
  const bodyMaxHeight = Math.max(MIN_BODY_HEIGHT_PX, maxPopoverHeight - headerHeight);

  popover.style.setProperty('max-height', `${maxPopoverHeight}px`, 'important');
  popover.style.setProperty('height', 'auto', 'important');
  popover.style.setProperty('display', 'flex', 'important');
  popover.style.setProperty('flex-direction', 'column', 'important');
  popover.style.setProperty('overflow', 'hidden', 'important');

  const body = popover.querySelector('.fc-popover-body');
  if (body instanceof HTMLElement) {
    body.style.setProperty('flex', '1 1 auto', 'important');
    body.style.setProperty('min-height', '0', 'important');
    body.style.setProperty('max-height', `${bodyMaxHeight}px`, 'important');
    body.style.setProperty('overflow-y', 'auto', 'important');
    body.style.setProperty('overflow-x', 'hidden', 'important');
  }
}

function constrainMorePopoverHorizontal(popover: HTMLElement, calendarContainer: HTMLElement) {
  if (!shouldConstrainMorePopoverHorizontal(calendarContainer)) {
    return;
  }

  const padding = POPOVER_EDGE_PADDING_PX;
  const viewportWidth = window.innerWidth;
  const maxWidth = viewportWidth - padding * 2;
  const rect = popover.getBoundingClientRect();
  const popoverWidth = Math.min(rect.width, maxWidth);
  const clampedLeft = Math.min(
    Math.max(rect.left, padding),
    Math.max(padding, viewportWidth - padding - popoverWidth)
  );

  popover.style.setProperty('position', 'fixed', 'important');
  popover.style.setProperty('left', `${clampedLeft}px`, 'important');
  popover.style.setProperty('top', `${rect.top}px`, 'important');
  popover.style.setProperty('max-width', `${maxWidth}px`, 'important');
  popover.style.setProperty('width', `${popoverWidth}px`, 'important');
  popover.style.setProperty('box-sizing', 'border-box', 'important');
}

function syncMorePopoverInContainer(calendarContainer: HTMLElement) {
  const popover = findMorePopover(calendarContainer);
  if (popover) {
    constrainMorePopover(popover, calendarContainer);
    constrainMorePopoverHorizontal(popover, calendarContainer);
  }
}

export function applyCalendarMorePopoverMaxHeight(calendarContainer: HTMLElement | null) {
  if (!calendarContainer) return;

  for (const delay of POPOVER_SYNC_DELAYS_MS) {
    window.setTimeout(() => {
      syncMorePopoverInContainer(calendarContainer);
    }, delay);
  }
}
